import type { NavigateFunction } from 'react-router-dom'

import { FolderIcon } from '@buffer-mono/popcorn'

import type { ChannelGroup_ChannelGroup } from '~publish/pages/Settings/ChannelGroups/hooks/useChannelGroups'

import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction } from './types'

const CHANNEL_FILTERABLE_PATHS = ['/schedule', '/all-channels']

function supportsChannelFiltering(pathname: string): boolean {
  return CHANNEL_FILTERABLE_PATHS.some((path) => pathname.startsWith(path))
}

/**
 * Options for building channel group actions.
 */
interface BuildChannelGroupActionsOptions {
  /** List of channel groups to create actions for */
  channelGroups: ChannelGroup_ChannelGroup[] | null
  /** React Router navigate function for navigation */
  navigate: NavigateFunction
  /** Current pathname for context-aware filtering */
  currentPathname?: string
}

/**
 * Builds command bar actions for all user channel groups.
 * Each group either filters the current page by the group's channels,
 * or navigates to all-channels with those channels filtered.
 *
 * @param options - Configuration options
 * @param options.channelGroups - List of channel groups to create actions for
 * @param options.navigate - React Router navigate function
 * @param options.currentPathname - Current URL pathname for context-aware filtering
 * @returns Array of command bar actions for channel groups
 */
export function buildChannelGroupActions({
  channelGroups,
  navigate,
  currentPathname = '',
}: BuildChannelGroupActionsOptions): CommandBarAction[] {
  if (!channelGroups || channelGroups.length === 0) {
    return []
  }

  const actions: CommandBarAction[] = []

  for (const group of channelGroups) {
    const channelNames =
      group.channels?.map((channel) => channel.name?.toLowerCase() ?? '') ?? []
    const channelCount = group.channelCount ?? group.channels?.length ?? 0
    const channelIds = group.channels?.map((channel) => channel.id) ?? []

    actions.push(
      createAction({
        id: `channel-group-${group.id}`,
        name: group.name,
        keywords: [
          group.name.toLowerCase(),
          'group',
          'channel group',
          'folder',
          ...channelNames,
        ],
        icon: <FolderIcon />,
        section: 'channel-groups',
        priority: PRIORITIES.channelGroups,
        description: `${channelCount} channel${channelCount === 1 ? '' : 's'}`,
        perform: () => {
          if (
            supportsChannelFiltering(currentPathname) &&
            channelIds.length > 0
          ) {
            const params = new URLSearchParams()
            channelIds.forEach((id) => params.append('channels[]', id))
            navigate({ pathname: currentPathname, search: params.toString() })
          } else {
            const params = new URLSearchParams()
            channelIds.forEach((id) => params.append('channels[]', id))
            navigate(`/all-channels?${params.toString()}`)
          }
        },
      }),
    )
  }

  return actions
}
